/**
 * DiagnosticsPanel (left column) — replaces the old fixed-bottom overlay.
 *
 * TRUTH RULE: only display real, measurable telemetry.
 *   • Event count      — real: diagnostics.length
 *   • Response latency — real: last "response.generated" → "audio.generation_started"
 *   • Playback health  — derived from playback.* events
 *   • JS heap (MB)     — only when performance.memory is available (Chrome).
 *                        Bar shows heap.used / heap.limit. Omitted otherwise.
 *   • No fabricated CPU/NET values.
 *
 * The full timeline & JSON export live inside a collapsible drawer.
 */

import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Download, Activity } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PanelShell } from "./PanelShell";
import { downloadDiagnosticsJson } from "@/lib/diagnosticsExport";

function ActivitySparkline({ events = [], windowMs = 60_000 }) {
    // 30 buckets over the last minute.
    const now = Date.now();
    const buckets = new Array(30).fill(0);
    const start = now - windowMs;
    for (const e of events) {
        if (e.ts < start) continue;
        const idx = Math.min(
            29,
            Math.max(0, Math.floor(((e.ts - start) / windowMs) * 30)),
        );
        buckets[idx]++;
    }
    const max = Math.max(1, ...buckets);
    return (
        <svg width="100%" height="26" viewBox="0 0 200 26" preserveAspectRatio="none" aria-hidden="true">
            {buckets.map((v, i) => {
                const x = (i / 30) * 200;
                const bw = 200 / 30 - 1;
                const bh = (v / max) * 22;
                return (
                    <rect
                        key={i}
                        x={x}
                        y={26 - bh}
                        width={bw}
                        height={bh}
                        fill="rgba(0,229,255,0.75)"
                        rx="0.5"
                    />
                );
            })}
        </svg>
    );
}

function computeLastResponseLatencyMs(events) {
    // Find the most recent audio.generation_started and pair with the
    // preceding response.generated (same response_id).
    for (let i = events.length - 1; i >= 0; i--) {
        const e = events[i];
        if (e.type !== "audio.generation_started") continue;
        for (let j = i - 1; j >= 0; j--) {
            const prev = events[j];
            if (prev.type === "response.generated" && prev.response_id === e.response_id) {
                return Math.max(0, e.ts - prev.ts);
            }
        }
    }
    return null;
}

function useHeap() {
    const [heap, setHeap] = useState(null);
    useEffect(() => {
        if (!performance || !performance.memory) return;
        const id = setInterval(() => {
            const m = performance.memory;
            setHeap({
                used: m.usedJSHeapSize,
                total: m.totalJSHeapSize,
                limit: m.jsHeapSizeLimit,
            });
        }, 1500);
        return () => clearInterval(id);
    }, []);
    return heap;
}

/**
 * Reads QorynOrbWebGL's mutable stats ref at ~2Hz without re-rendering
 * every frame. The orb writes to the ref directly (spec §30) so we don't
 * pay a React state update per frame.
 */
function useEngineStats(engineStatsRef) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        if (!engineStatsRef) return undefined;
        const id = setInterval(() => {
            const s = engineStatsRef.current;
            if (s) setStats({ ...s });
        }, 500);
        return () => clearInterval(id);
    }, [engineStatsRef]);
    return stats;
}

function downloadJson(diagnostics, base) {
    // Delegate to the shared helper. `base` is derived internally from
    // sessionStartedAt or the first event, matching the previous inline
    // implementation. We reconstruct sessionStartedAt from `base` so the
    // relative timestamps line up 1:1 with the older exports.
    return downloadDiagnosticsJson(diagnostics, base);
}

export function LeftDiagnosticsPanel({ diagnostics = [], sessionStartedAt, engineStatsRef, intensity = 1.0, onIntensityChange }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const heap = useHeap();
    const engineStats = useEngineStats(engineStatsRef);
    const total = diagnostics.length;
    const base = sessionStartedAt ?? (diagnostics[0]?.ts ?? null);

    const latencyMs = useMemo(
        () => computeLastResponseLatencyMs(diagnostics),
        [diagnostics],
    );

    const playbackHealthy = useMemo(() => {
        const bad = diagnostics.some(
            (d) => d.type === "playback.stalled" || d.type === "playback.error",
        );
        return !bad;
    }, [diagnostics]);

    const heapPct = heap ? heap.used / heap.limit : null;

    return (
        <PanelShell
            title="Diagnostics"
            testId="panel-diagnostics"
            right={
                <div className="flex items-center gap-2">
                    <span
                        className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted"
                        data-testid="diagnostics-count"
                    >
                        {total}
                    </span>
                    <button
                        data-testid="diagnostics-toggle-btn"
                        aria-expanded={drawerOpen}
                        onClick={() => setDrawerOpen((v) => !v)}
                        className="text-qoryn-textDim hover:text-qoryn-text transition-colors qoryn-focus"
                    >
                        {drawerOpen ? (
                            <ChevronUp className="h-3.5 w-3.5" strokeWidth={1.75} />
                        ) : (
                            <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.75} />
                        )}
                    </button>
                </div>
            }
        >
            {/* Activity strip */}
            <div className="mb-2">
                <ActivitySparkline events={diagnostics} />
            </div>

            {/* Metric bars */}
            <div className="space-y-1.5" data-testid="diagnostics-metrics">
                <MetricRow
                    label="LATENCY"
                    value={latencyMs != null ? `${latencyMs} ms` : "—"}
                    fillPct={
                        latencyMs != null
                            ? Math.min(1, latencyMs / 1200)
                            : 0
                    }
                    tone="cyan"
                    testId="metric-latency"
                />
                <MetricRow
                    label="PLAYBACK"
                    value={playbackHealthy ? "Healthy" : "Impaired"}
                    fillPct={playbackHealthy ? 1 : 0.35}
                    tone={playbackHealthy ? "mint" : "warn"}
                    testId="metric-playback"
                />
                {heap && (
                    <MetricRow
                        label="JS HEAP"
                        value={`${(heap.used / 1e6).toFixed(1)} MB`}
                        fillPct={heapPct ?? 0}
                        tone="cyan"
                        testId="metric-heap"
                    />
                )}
                <MetricRow
                    label="EVENTS"
                    value={String(total)}
                    fillPct={Math.min(1, total / 200)}
                    tone="cyan"
                    testId="metric-events"
                />
                {engineStats && (
                    <>
                        <MetricRow
                            label="ORB FPS"
                            value={`${engineStats.fps}`}
                            fillPct={Math.min(1, engineStats.fps / 60)}
                            tone={engineStats.fps >= 50 ? "mint" : "warn"}
                            testId="metric-orb-fps"
                        />
                        <MetricRow
                            label="ORB RMS"
                            value={engineStats.rms.toFixed(2)}
                            fillPct={Math.min(1, engineStats.rms * 1.6)}
                            tone="cyan"
                            testId="metric-orb-rms"
                        />
                        <MetricRow
                            label="TRANSIENT"
                            value={engineStats.transient.toFixed(2)}
                            fillPct={Math.min(1, engineStats.transient * 1.4)}
                            tone="cyan"
                            testId="metric-orb-transient"
                        />
                    </>
                )}
            </div>

            {engineStats && (
                <div
                    className="mt-2 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted flex items-center justify-between"
                    data-testid="engine-stats-line"
                >
                    <span>
                        {engineStats.quality} · {engineStats.particles}p ·{" "}
                        {engineStats.rings}r · DPR {engineStats.dpr.toFixed(1)}
                    </span>
                    <span
                        className={
                            engineStats.analyserAttached
                                ? "text-qoryn-mint"
                                : "text-qoryn-textMuted"
                        }
                    >
                        {engineStats.analyserAttached ? "audio ✓" : "audio ·"}
                    </span>
                </div>
            )}

            {/* Drawer with full timeline + export */}
            <AnimatePresence>
                {drawerOpen && (
                    <motion.div
                        key="drawer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-3 overflow-hidden"
                    >
                        <div className="flex items-center justify-between mb-2 pt-2 border-t border-white/5">
                            <div className="flex items-center gap-1.5">
                                <Activity className="h-3 w-3 text-qoryn-cyan" strokeWidth={1.75} />
                                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                                    Timeline
                                </span>
                            </div>
                            <button
                                data-testid="diagnostics-export-btn"
                                onClick={() => downloadJson(diagnostics, base)}
                                className="flex items-center gap-1 text-qoryn-textDim hover:text-qoryn-text transition-colors qoryn-focus"
                                aria-label="Export diagnostics"
                            >
                                <Download className="h-3 w-3" strokeWidth={1.75} />
                                <span className="font-mono-ui text-[9px] tracking-widest uppercase">
                                    Export
                                </span>
                            </button>
                        </div>

                        {/* Visual Intensity slider — spec §22 stretch. Value
                            is a multiplier applied to every audio-reactive
                            uniform in the orb. Persisted in localStorage. */}
                        {onIntensityChange && (
                            <div
                                className="mb-3 pb-3 border-b border-white/5"
                                data-testid="visual-intensity-control"
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim">
                                        Visual intensity
                                    </span>
                                    <span
                                        className="font-mono-ui text-[10px] tabular-nums text-qoryn-cyan"
                                        data-testid="visual-intensity-value"
                                    >
                                        {intensity.toFixed(2)}×
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min={0.2}
                                    max={2.0}
                                    step={0.05}
                                    value={intensity}
                                    onChange={(e) => onIntensityChange(parseFloat(e.target.value))}
                                    data-testid="visual-intensity-slider"
                                    aria-label="Visual intensity"
                                    className="w-full accent-qoryn-cyan"
                                />
                                <div className="flex justify-between mt-0.5">
                                    <span className="font-mono-ui text-[8px] text-qoryn-textMuted">calm</span>
                                    <button
                                        type="button"
                                        onClick={() => onIntensityChange(1.0)}
                                        data-testid="visual-intensity-reset"
                                        className="font-mono-ui text-[8px] text-qoryn-textDim hover:text-qoryn-cyan transition-colors qoryn-focus"
                                    >
                                        reset
                                    </button>
                                    <span className="font-mono-ui text-[8px] text-qoryn-textMuted">intense</span>
                                </div>
                            </div>
                        )}
                        <div
                            data-testid="diagnostics-list"
                            className="max-h-[180px] overflow-y-auto hide-scrollbar space-y-0.5 font-mono-ui text-[10px]"
                        >
                            {diagnostics.length === 0 && (
                                <div className="text-qoryn-textMuted">No events yet.</div>
                            )}
                            {diagnostics.slice(-40).map((d, i) => (
                                <div
                                    key={`${d.ts}-${i}`}
                                    data-testid="diagnostics-event"
                                    data-event-type={d.type}
                                    className="flex items-center gap-2"
                                >
                                    <span className="text-qoryn-textMuted w-12 shrink-0">
                                        {base
                                            ? `+${((d.ts - base) / 1000).toFixed(2)}s`
                                            : "+0.00s"}
                                    </span>
                                    <span className="text-qoryn-text truncate">
                                        {d.type}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </PanelShell>
    );
}

function MetricRow({ label, value, fillPct = 0, tone = "cyan", testId }) {
    const color =
        tone === "mint"
            ? "rgba(94,247,198,0.85)"
            : tone === "warn"
              ? "rgba(234,179,8,0.85)"
              : "rgba(0,229,255,0.85)";
    return (
        <div className="flex items-center gap-2" data-testid={testId}>
            <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted w-16 shrink-0">
                {label}
            </span>
            <div className="relative flex-1 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                <div
                    className="absolute inset-y-0 left-0"
                    style={{
                        width: `${Math.max(4, fillPct * 100)}%`,
                        background: color,
                        transition: "width 300ms ease",
                    }}
                />
            </div>
            <span className="font-mono-ui text-[10px] text-qoryn-text w-16 text-right tabular-nums">
                {value}
            </span>
        </div>
    );
}

export default LeftDiagnosticsPanel;
